/**
 * useRecentTools — client-side "Jump back in" history for the Home hub
 * (X.6.3). Records which hub tiles the user opened, newest-first, so Home can
 * surface a few recent tools.
 *
 * Backed by localStorage (this is the production app, not a sandbox artifact —
 * persistent client storage is appropriate). No backend, no cross-device sync;
 * if cross-device history is ever wanted that's a future server concern. Keyed
 * by tile id; the consumer maps ids back to live tiles and re-applies its own
 * capability gating, so a tile the user can no longer see never resurfaces.
 *
 * SSR/jsdom-safe: a missing or throwing localStorage degrades to an empty
 * history (the row simply hides) rather than crashing the page.
 */

import { useCallback, useState } from 'react';
import { RECENT_TOOLS_KEY } from '@/lib/keptAcrossSignOut';

const STORAGE_KEY = RECENT_TOOLS_KEY;
const MAX_STORED = 12;

export interface RecentToolEntry {
  id: string;
  /** Epoch ms when the tile was last opened. */
  ts: number;
}

function readStore(): RecentToolEntry[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentToolEntry =>
        !!e && typeof e === 'object' && typeof (e as RecentToolEntry).id === 'string' &&
        typeof (e as RecentToolEntry).ts === 'number',
    );
  } catch {
    return [];
  }
}

function writeStore(entries: RecentToolEntry[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota/private-mode failures are non-fatal — history is best-effort.
  }
}

export interface UseRecentTools {
  /** Newest-first, deduped by id. */
  entries: RecentToolEntry[];
  /** Record an open of `id` (moves it to the front with a fresh timestamp). */
  record: (id: string) => void;
}

export function useRecentTools(): UseRecentTools {
  const [entries, setEntries] = useState<RecentToolEntry[]>(() => readStore());

  const record = useCallback((id: string): void => {
    setEntries((prev) => {
      const next: RecentToolEntry[] = [
        { id, ts: Date.now() },
        ...prev.filter((e) => e.id !== id),
      ].slice(0, MAX_STORED);
      writeStore(next);
      return next;
    });
  }, []);

  return { entries, record };
}

/** Compact relative-time label ("just now", "20 minutes ago", "yesterday"). */
export function relativeTime(ts: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - ts);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day} days ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk} week${wk === 1 ? '' : 's'} ago`;
  return 'a while ago';
}
